import { useMemo } from "react";
import type { DepositRequired, VenueConfig } from "../../../common/jsonRpcTypes";
import type { PendingBooking } from "../bookingForm/BookingForm";

/**
 * Parameter interfaces for useBookingData hook
 */
interface BookingParams {
  booking: PendingBooking | null;
  pendingBookingId: string | null;
}

interface VenueParams {
  venueConfig: VenueConfig | null;
  currency: string;
}

interface PaymentParams {
  depositRequired: DepositRequired | null | undefined;
  paymentProcessor: string;
  isAuthOnly: boolean;
  isDepositTaxExclusive: boolean;
}

/**
 * Trims the venue's deposit terms and treats blank or whitespace-only text as
 * absent, so the confirmation never renders an empty alert. Note this cannot be
 * a nullish coalesce: an empty string must be discarded, not passed through.
 */
const normaliseDepositTerms = (terms: string | undefined): string | undefined => {
  const trimmed = terms?.trim();
  return trimmed === "" ? undefined : trimmed;
};

/**
 * Handle booking data object construction for payment processors.
 * Extracted from PaymentStep to reduce cognitive complexity.
 */
export const useBookingData = (
  bookingParams: BookingParams,
  venueParams: VenueParams,
  paymentParams: PaymentParams,
) => {
  return useMemo(
    () => ({
      depositTotal:
        paymentParams.isDepositTaxExclusive && paymentParams.depositRequired?.gross_amount != null
          ? paymentParams.depositRequired.gross_amount
          : (paymentParams.depositRequired?.total ?? 0),
      guestCount: bookingParams.booking?.num_people ?? 0,
      time: bookingParams.booking?.time ?? "",
      date: bookingParams.booking?.date ?? "",
      bookingId: bookingParams.pendingBookingId ?? "",
      bookingRef: bookingParams.pendingBookingId ?? "",
      venue: {
        title: venueParams.venueConfig?.title ?? "",
        currency: venueParams.currency,
        country_code: null,
        // Preserve the original collins_features when present (whether a legacy
        // string[] or a Record). Only synthesise a Record from the dedicated
        // when collins_features is entirely absent, to avoid silently dropping
        // non-fpay features (e.g. `payment_amex_enabled`) carried in arrays.
        collins_features:
          venueParams.venueConfig?.collins_features !== undefined
            ? venueParams.venueConfig.collins_features
            : {
                fpay_apple_pay:
                  venueParams.venueConfig?.enableApplePay === true ||
                  venueParams.venueConfig?.enableApplePay === "true",
                fpay_google_pay:
                  venueParams.venueConfig?.enableGooglePay === true ||
                  venueParams.venueConfig?.enableGooglePay === "true",
                fpay_mobile_payments: !!venueParams.venueConfig?.enableMobilePaymentMethods,
              },
      },
      paymentProcessor: paymentParams.paymentProcessor,
      depositTypePayment: paymentParams.isAuthOnly ? "false" : "true",
      // Carried through so the in-page confirmation can repeat the terms the
      // guest agreed to at the payment step.
      depositTerms: normaliseDepositTerms(paymentParams.depositRequired?.terms),
    }),
    [
      paymentParams.depositRequired,
      bookingParams.booking,
      bookingParams.pendingBookingId,
      venueParams.venueConfig,
      paymentParams.paymentProcessor,
      paymentParams.isAuthOnly,
      venueParams.currency,
      paymentParams.isDepositTaxExclusive,
    ],
  );
};
